import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import '../styles/insights.css';

export default function InsightAppDev() {
  return (
    <article className="insight-article container container--narrow section">
      <Seo
        title="앱 개발 방식 비교 가이드"
        description="네이티브, 크로스플랫폼(Flutter·React Native), 웹앱의 차이와 장단점. 어떤 상황에 어떤 방식이 맞는지 판단 기준을 정리했습니다."
        path="/insights/app-development"
      />
      <header className="insight-head">
        <span className="insight-eyebrow">기술 선택</span>
        <h1>앱 개발 방식 비교 — 네이티브·크로스플랫폼·웹앱</h1>
        <p className="insight-lead">
          앱을 만들기로 결정하고 나면 곧바로 다음 질문이 따라옵니다. 네이티브로 만들 것인가,
          크로스플랫폼으로 만들 것인가, 아니면 웹으로도 충분한가. 이 선택은 초기 개발비와 일정뿐
          아니라 출시 이후 몇 년간의 운영 비용까지 결정합니다. 각 방식이 실제로 무엇이 다른지,
          어떤 상황에서 어떤 선택이 합리적인지 정리했습니다.
        </p>
      </header>

      <h2>세 가지 방식 한눈에 보기</h2>
      <p>
        먼저 큰 그림입니다. 아래 표의 비용은 절대 금액이 아니라 상대 비율입니다. 같은 기능을
        네이티브로 만들 때를 100으로 두고 비교한 것이며, 프로젝트의 화면 수와 서버 연동 범위에 따라
        차이가 크게 벌어질 수 있습니다.
      </p>

      <div className="insight-table">
        <table>
          <thead>
            <tr>
              <th>비교 항목</th>
              <th>네이티브</th>
              <th>크로스플랫폼</th>
              <th>웹앱 / PWA</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>사용 기술</td>
              <td>Swift(iOS), Kotlin(Android)</td>
              <td>Flutter, React Native</td>
              <td>HTML·CSS·JavaScript</td>
            </tr>
            <tr>
              <td>개발 비용(상대)</td>
              <td>100</td>
              <td>60~75</td>
              <td>40~60</td>
            </tr>
            <tr>
              <td>iOS·Android 동시 대응</td>
              <td>각각 따로 개발</td>
              <td>코드 대부분 공유</td>
              <td>브라우저 하나로 대응</td>
            </tr>
            <tr>
              <td>성능</td>
              <td>가장 유리</td>
              <td>일반 업무 앱에는 충분</td>
              <td>무거운 화면에서 한계</td>
            </tr>
            <tr>
              <td>기기 기능 접근</td>
              <td>제약 없음</td>
              <td>대부분 가능(일부 추가 개발)</td>
              <td>제약 큼</td>
            </tr>
            <tr>
              <td>유지보수 부담</td>
              <td>두 벌을 각각 관리</td>
              <td>한 벌 + 플랫폼별 예외</td>
              <td>한 벌</td>
            </tr>
            <tr>
              <td>앱스토어 등록</td>
              <td>필요</td>
              <td>필요</td>
              <td>불필요(스토어 노출도 없음)</td>
            </tr>
            <tr>
              <td>업데이트 반영</td>
              <td>심사 후 배포</td>
              <td>심사 후 배포</td>
              <td>즉시 반영</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>네이티브 개발 — 각 운영체제의 언어로 따로 만듭니다</h2>
      <p>
        네이티브는 iOS는 Swift로, Android는 Kotlin으로 각각 개발하는 방식입니다. 애플과 구글이
        직접 제공하는 개발 도구와 화면 구성 방식을 그대로 사용하기 때문에, 운영체제가 낼 수 있는
        성능을 온전히 쓸 수 있고 새로 나온 기능도 가장 먼저 적용할 수 있습니다.
      </p>
      <p>
        대신 같은 기능을 두 번 만듭니다. 화면 하나를 수정하면 iOS 담당자와 Android 담당자가 각각
        작업하고, 각각 테스트하고, 각각 배포합니다. 개발비는 크로스플랫폼 대비 대체로 1.4~1.7배
        수준이며, 출시 이후 유지보수 비용도 같은 비율로 계속 발생합니다. 초기 비용보다 이
        &lsquo;계속 발생하는 부분&rsquo;이 더 중요할 때가 많습니다.
      </p>

      <h3>네이티브가 확실히 유리한 경우</h3>
      <ul>
        <li><strong>고성능 그래픽</strong> — 3D 렌더링, 게임, 실시간 영상 편집처럼 초당 프레임을 끝까지 짜내야 하는 앱입니다.</li>
        <li><strong>카메라·센서 심화 활용</strong> — 실시간 필터, 증강현실, 정밀한 위치 추적, 사용자 지정 카메라 제어가 핵심 기능인 경우입니다.</li>
        <li><strong>특수 하드웨어 연동</strong> — 블루투스 기기 제어, NFC 태그, 결제 단말, 의료기기 연동 등 제조사 SDK를 직접 붙여야 하는 경우입니다.</li>
        <li><strong>최신 OS 기능을 즉시 써야 할 때</strong> — 위젯, 워치 앱, 시스템 확장 기능 등은 크로스플랫폼 지원이 늦거나 제한적입니다.</li>
        <li><strong>백그라운드 동작이 중요할 때</strong> — 장시간 위치 추적, 대용량 백그라운드 업로드처럼 운영체제 정책과 밀접한 영역입니다.</li>
      </ul>
      <p>
        여기에 해당하지 않는데도 습관적으로 네이티브를 선택하면, 같은 예산으로 만들 수 있었던 기능의
        절반만 만들고 출시하게 됩니다.
      </p>

      <h2>크로스플랫폼 — 하나의 코드로 두 플랫폼을 동시에</h2>
      <p>
        크로스플랫폼은 코드를 한 벌만 작성해 iOS와 Android 앱을 함께 만드는 방식입니다. 현재 실무에서
        선택지는 사실상 두 가지입니다.
      </p>
      <ul>
        <li><strong>Flutter</strong> — 구글이 만든 도구로, 화면을 자체 엔진으로 직접 그립니다. 두 플랫폼에서 화면이 거의 동일하게 보이고, 애니메이션이 많은 화면에서 안정적입니다. Dart라는 별도 언어를 씁니다.</li>
        <li><strong>React Native</strong> — 메타가 만든 도구로, JavaScript로 작성하되 실제 화면은 각 운영체제의 기본 요소로 그립니다. 웹 프론트엔드 인력과 기술 스택이 이어지는 것이 장점입니다.</li>
      </ul>
      <p>
        실제 프로젝트에서 코드 공유율은 대체로 80~90% 정도입니다. 나머지 10~20%는 푸시 알림 설정,
        결제 연동, 권한 요청, 로그인 방식처럼 플랫폼별로 따로 손대야 하는 영역입니다. 즉
        &lsquo;한 번 만들면 끝&rsquo;이 아니라 &lsquo;대부분을 공유하고 일부를 나눠 만든다&rsquo;에
        가깝습니다.
      </p>

      <div className="insight-callout">
        <strong>크로스플랫폼은 만능이 아닙니다</strong>
        <p>
          앱 실행 파일 용량이 네이티브보다 큰 편이고, 첫 실행 속도가 조금 더 느릴 수 있습니다.
          운영체제에 새 기능이 나오면 이를 지원하는 라이브러리가 나올 때까지 몇 달을 기다리거나,
          결국 네이티브 코드를 직접 붙여야 하는 상황도 생깁니다. 특수 하드웨어 연동이나 고성능
          그래픽이 서비스의 핵심이라면 네이티브가 여전히 정답입니다.
        </p>
      </div>

      <p>
        그럼에도 예약, 주문, 커뮤니티, 사내 업무, 회원 관리, 콘텐츠 열람처럼 화면과 서버 통신이
        중심인 비즈니스 앱이라면 크로스플랫폼으로 충분한 경우가 대부분입니다. 사용자는 어떤 기술로
        만들었는지 알 수 없고, 알 필요도 없습니다.
      </p>

      <h3>Flutter와 React Native 중에서 고른다면</h3>
      <p>
        둘 중 무엇이 더 낫다고 단정하기는 어렵습니다. 판단은 기술의 우열보다 프로젝트의 조건에서
        갈립니다. 화면 구성이 독자적이고 애니메이션 비중이 크며, 두 플랫폼에서 완전히 같은 화면을
        원한다면 Flutter가 편합니다. 이미 웹 서비스를 React로 운영하고 있어 인력과 코드 일부를
        재사용하고 싶다면 React Native가 유리합니다.
      </p>
      <p>
        더 중요한 기준은 따로 있습니다. 앞으로 이 앱을 누가 계속 손보게 될 것인가입니다. 국내에서
        구하기 쉬운 인력, 사내에 이미 있는 기술, 협력사가 익숙한 도구가 무엇인지에 따라 몇 년 뒤
        유지보수 난이도가 크게 달라집니다. 개발사가 특정 도구를 제안한다면 왜 그것을 골랐는지, 다른
        선택지와 비교해 무엇을 포기했는지 물어보시기 바랍니다. 납득할 만한 답이 돌아오지 않는다면
        그 판단은 프로젝트가 아니라 개발사의 편의에서 나왔을 가능성이 있습니다.
      </p>

      <h2>웹앱과 PWA — 앱스토어 없이 배포하는 선택지</h2>
      <p>
        웹앱은 브라우저에서 동작하는 서비스입니다. 여기에 PWA(프로그레시브 웹 앱) 기술을 더하면
        홈 화면에 아이콘을 추가할 수 있고, 주소창 없이 전체 화면으로 열리며, 일부 데이터를 저장해
        네트워크가 끊겨도 화면이 뜨게 만들 수 있습니다. 겉모습만 보면 앱과 구분이 어렵습니다.
      </p>
      <p>강점은 분명합니다.</p>
      <ul>
        <li><strong>심사가 없습니다</strong> — 고치면 바로 반영됩니다. 급한 오류 수정에 며칠을 기다리지 않아도 됩니다.</li>
        <li><strong>링크로 공유됩니다</strong> — 설치 과정 없이 주소만 보내면 됩니다. 초기 사용자 확보 장벽이 가장 낮습니다.</li>
        <li><strong>검색에 노출됩니다</strong> — 앱은 검색엔진에 잡히지 않지만 웹은 잡힙니다.</li>
        <li><strong>비용이 가장 낮습니다</strong> — 코드가 한 벌이고 스토어 대응 작업이 없습니다.</li>
      </ul>
      <p>제약도 분명합니다.</p>
      <ul>
        <li><strong>푸시 알림</strong> — Android는 비교적 자유롭지만, iOS에서는 사용자가 홈 화면에 직접 추가해야 알림을 받을 수 있습니다. 알림이 서비스의 핵심 동력이라면 위험한 선택입니다.</li>
        <li><strong>백그라운드 동작</strong> — 앱이 닫힌 상태에서 무언가를 계속 수행하는 일은 사실상 불가능합니다.</li>
        <li><strong>기기 기능 접근</strong> — 블루투스, NFC, 건강 데이터, 연락처 같은 영역은 접근이 막혀 있거나 브라우저마다 다릅니다.</li>
        <li><strong>스토어 노출이 없습니다</strong> — 앱스토어 검색으로 유입되는 사용자를 포기하는 것이며, &lsquo;앱이 있다&rsquo;는 신뢰 요소도 얻지 못합니다.</li>
      </ul>
      <p>
        그래서 웹앱은 종착지가 아니라 출발점으로 쓰기 좋습니다. 웹으로 먼저 만들어 수요를 확인하고,
        사용자가 붙기 시작하면 앱으로 확장하는 순서가 실패 비용이 가장 작습니다.
      </p>

      <h2>어떤 기준으로 고를까</h2>
      <p>
        기술이 좋아서 고르는 것이 아니라, 조건에 맞아서 고르는 것입니다. 실제로 판단에 영향을 주는
        기준은 네 가지 정도입니다.
      </p>

      <h3>1. 예산</h3>
      <p>
        예산이 빠듯하다면 만들 수 있는 방식이 아니라 유지할 수 있는 방식을 기준으로 삼아야 합니다.
        네이티브 두 벌은 개발 이후에도 계속 두 배의 손이 들어갑니다. 예산 규모별로 무엇이 달라지는지는{' '}
        <Link to="/insights/cost">웹·앱 개발 비용과 기간은 어떻게 정해지나</Link> 글에서 더 자세히
        다뤘습니다.
      </p>

      <h3>2. 출시 기간</h3>
      <p>
        시연이나 투자 유치처럼 날짜가 정해진 목표가 있다면 심사 일정이 없는 웹앱이 가장 안전합니다.
        앱 형태가 반드시 필요하다면 크로스플랫폼으로 두 플랫폼을 한 번에 가져가는 편이 일정 관리에
        유리합니다.
      </p>

      <h3>3. 성능 요구</h3>
      <p>
        &lsquo;빠르게 느껴지면 좋겠다&rsquo;와 &lsquo;60프레임이 무너지면 서비스가 성립하지
        않는다&rsquo;는 전혀 다른 요구입니다. 후자에 해당한다면 네이티브를 검토해야 합니다. 대부분의
        비즈니스 앱은 전자에 속하며, 이 경우 체감 속도는 개발 방식보다 서버 응답 속도와 이미지 처리에서
        더 크게 갈립니다.
      </p>

      <h3>4. 향후 확장</h3>
      <p>
        1년 뒤 무엇을 붙일 계획인지 미리 적어 보시기 바랍니다. 워치 앱, 블루투스 기기 연동, 오프라인
        결제 단말처럼 하드웨어와 얽히는 계획이 있다면 처음부터 네이티브 비중을 높이는 편이 낫습니다.
        반대로 기능 추가가 대부분 화면과 서버 로직 수준이라면 크로스플랫폼이 오래 버팁니다.
      </p>

      <h2>앱스토어 심사라는 변수</h2>
      <p>
        기술 방식을 정할 때 자주 빠지는 항목이 심사 일정입니다. 앱을 만든 시점과 사용자가 내려받을 수
        있는 시점 사이에는 반드시 간격이 있습니다.
      </p>
      <ul>
        <li><strong>애플 앱스토어</strong> — 검토에 보통 하루에서 사흘 정도 걸리지만, 반려되면 수정 후 다시 대기해야 합니다. 개인정보 수집 항목 누락, 회원가입만 있고 실제로 볼 수 있는 내용이 부족한 경우, 외부 결제 유도, 계정 삭제 기능 누락 등이 흔한 반려 사유입니다.</li>
        <li><strong>구글 플레이</strong> — 상대적으로 빠른 편이지만 신규 개발자 계정에는 사전 테스트 요건이 붙는 등 정책이 계속 강화되고 있어, 첫 등록은 예상보다 오래 걸릴 수 있습니다.</li>
        <li><strong>정책 변경</strong> — 개인정보 처리방침, 데이터 안전 정보, 연령 등급 같은 항목은 규정이 수시로 바뀝니다. 등록해 두고 방치하면 어느 날 노출이 중단될 수 있습니다.</li>
      </ul>
      <p>
        따라서 앱 형태로 출시한다면 일정에 1~2주 정도 여유를 두는 편이 안전합니다. 반려 자체는
        드문 일이 아니며, 첫 등록에서 한두 번 되돌아오는 경우도 흔합니다. 문제는 반려가 아니라 반려를
        예상하지 못한 일정입니다. 출시 이후에 필요한 운영 업무는{' '}
        <Link to="/insights/maintenance">출시가 끝이 아닙니다: 운영과 유지보수 가이드</Link>에서
        따로 정리했습니다.
      </p>

      <h2>실제 선택 예시 세 가지</h2>

      <h3>시나리오 A — 동네 매장의 예약·주문 서비스</h3>
      <p>
        단골 손님이 메뉴를 보고 예약하거나 미리 주문하는 서비스입니다. 사용자 대부분이 매장 링크나
        메신저를 통해 들어오고, 방문 빈도는 주 1~2회 수준입니다. 이 경우 웹앱 또는 PWA로 시작하는
        편이 합리적입니다. 설치를 요구하지 않으니 이탈이 적고, 메뉴나 가격을 바꿔도 심사 없이 바로
        반영됩니다. 알림이 꼭 필요하다면 초기에는 문자나 카카오 알림톡으로 대체하고, 재방문율이 충분히
        확인된 뒤에 앱을 검토해도 늦지 않습니다.
      </p>

      <h3>시나리오 B — 현장 직원이 쓰는 사내 업무 앱</h3>
      <p>
        출퇴근 기록, 작업 지시 확인, 사진 첨부 보고를 처리하는 앱입니다. 사용자는 회사 인원으로
        한정되지만 iOS와 Android가 섞여 있고, 카메라와 위치 정보를 사용하며 푸시 알림이 업무 흐름의
        핵심입니다. 이런 조합에는 크로스플랫폼이 가장 잘 맞습니다. 필요한 기기 기능은 검증된
        라이브러리로 대부분 해결되고, 코드가 한 벌이라 기능 추가 요청에 빠르게 대응할 수 있습니다.
        사내 배포 방식을 쓰면 스토어 심사 부담도 줄일 수 있습니다.
      </p>

      <h3>시나리오 C — 실시간 카메라 효과가 핵심인 앱</h3>
      <p>
        촬영 화면에서 실시간으로 얼굴을 인식해 효과를 입히고, 결과를 영상으로 저장하는 앱입니다. 여기서
        성능은 부가 요소가 아니라 서비스 그 자체입니다. 이런 경우에는 네이티브를 권합니다. 다만 전부
        네이티브로 만들 필요는 없습니다. 카메라와 영상 처리 부분만 네이티브로 구현하고, 로그인·설정·
        피드 같은 일반 화면은 크로스플랫폼으로 감싸는 혼합 구조도 실무에서 자주 쓰입니다.
      </p>

      <div className="insight-callout">
        <strong>정리하면</strong>
        <p>
          기기 성능을 끝까지 써야 하면 네이티브, 두 플랫폼에 같은 서비스를 합리적인 비용으로 올려야
          하면 크로스플랫폼, 아직 수요를 확인하는 단계이거나 설치 장벽을 없애야 하면 웹앱입니다.
          그리고 대부분의 비즈니스 앱은 두 번째와 세 번째 사이에 있습니다.
        </p>
      </div>

      <div className="insight-cta">
        <p>프로젝트를 구상 중이시라면 편하게 문의해 주세요. 아이디어 단계여도 괜찮습니다.</p>
        <Link to="/contact" className="insight-cta__btn">프로젝트 문의하기</Link>
      </div>

      <p className="insight-note">
        ※ 본문의 비용 비율과 기간은 일반적인 프로젝트를 기준으로 한 참고 값입니다. 기능 범위, 화면 수,
        서버 연동 정도, 디자인 완성도, 검수 절차에 따라 실제 수치는 프로젝트마다 크게 달라집니다.
        앱스토어 심사 기준과 정책은 수시로 변경되므로 진행 시점에 각 스토어의 최신 가이드라인을 확인해
        주시기 바랍니다.
      </p>
    </article>
  );
}
